import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import Tooltip from "ui/Tooltip/Tooltip";
import { PlacementType } from "ui/Tooltip/Tooltip";
import AttentionSignSmall from "assets/icons/attention-sign-small.svg";
import Plus from "assets/icons/plus.svg";
import Minus from "assets/icons/minus.svg";
import AttentionSign from "assets/icons/attention-sign.svg";
import { cn } from "helpers/style";
import styles from "./Counter.module.scss";

type Props = {
  min?: number;
  max?: number;
  value: number;
  disabled?: boolean;
  wide?: boolean;
  size?: "small" | "large";
  onChange?: (value: number) => void;
  minResetValue?: number;
  onReset?: () => void;
  tooltipPlacement?: PlacementType;
  className?: string;
  classNameInput?: string;
};

const Counter: React.FC<Props> = ({
  min = 0,
  max = 999,
  value,
  disabled = false,
  size,
  wide,
  onChange,
  minResetValue,
  onReset,
  tooltipPlacement = "top",
  className,
  classNameInput,
}: Props) => {
  const [innerValue, updateInnerValue] = useState(value);
  const maxCount = useMemo(() => Math.min(max, 999), [max]);

  useEffect(() => {
    updateInnerValue(value);
  }, [value]);

  function handleDec(e) {
    e && e.stopPropagation();

    if (minResetValue === innerValue - 1) {
      onReset && onReset();
      return;
    }

    if (innerValue > min) {
      onChange && onChange(innerValue - 1);
    }
  }

  function handleInc(e) {
    e && e.stopPropagation();
    if (innerValue < maxCount) {
      onChange && onChange(innerValue + 1);
    }
  }

  function handleSubmit(event: ChangeEvent<HTMLInputElement>) {
    const newValue = event.target.value
      ? Math.max(Math.min(event.target.valueAsNumber, maxCount), min)
      : null;

    if (newValue === value) return;

    if (event.target.value === String(minResetValue)) {
      onReset && onReset();
      updateInnerValue(min);
      return;
    }

    if (newValue === null) {
      reset();
      return;
    }

    updateInnerValue(newValue);
    onChange && onChange(newValue);
  }

  function reset() {
    updateInnerValue(value);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target) return;
    updateInnerValue(event.target.valueAsNumber);
  }

  function handleKeyPress(event) {
    if (event.key === "Enter") {
      event.target.blur();
      handleSubmit(event);
    }
  }

  return (
    <div className={cn([styles.counter, className])}>
      <button
        className={cn([
          styles.counterButton,
          size === "small" && styles.counterButtonSmall,
          size === "large" && styles.counterButtonLarge,
        ])}
        disabled={disabled}
        aria-label={`Уменьшить на один. Текущее количество ${innerValue}`}
        onClick={handleDec}
      >
        <div className={styles.counterCenter}>
          <Minus />
        </div>
      </button>

      <input
        className={cn([
          styles.counterField,
          size && size === "large" && styles.counterFieldLarge,
          wide && styles.counterFieldWide,
          classNameInput,
        ])}
        type="number"
        aria-labelledby="Задать количество"
        value={innerValue}
        onChange={handleChange}
        onBlur={handleSubmit}
        aria-label="Задать количество"
        onClick={e => e.stopPropagation()}
        onKeyPress={handleKeyPress}
        disabled={disabled}
      />

      {innerValue >= maxCount ? (
        <Tooltip
          title="Количество ограничено"
          placement={tooltipPlacement}
          maxWidth={216}
        >
          <button
            className={cn([
              styles.counterButton,
              size === "small" && styles.counterButtonSmall,
              size === "large" && styles.counterButtonLarge,
              styles.counterButtonLimited,
            ])}
            disabled={disabled}
          >
            <div className={styles.counterCenter}>
              {size === "small" ? <AttentionSignSmall /> : <AttentionSign />}
            </div>
          </button>
        </Tooltip>
      ) : (
        <button
          className={cn([
            styles.counterButton,
            size === "small" && styles.counterButtonSmall,
            size === "large" && styles.counterButtonLarge,
          ])}
          disabled={disabled}
          aria-label={`Увеличить на один. Текущее количество ${innerValue}`}
          onClick={handleInc}
        >
          <div className={styles.counterCenter}>
            <Plus />
          </div>
        </button>
      )}
    </div>
  );
};

export default Counter;
